// src/components/TableCajas.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Table,
} from "react-bootstrap";

import {
  getCajas,
  deleteCaja,
  getCajaById,
} from "../helpers/rutaCajas";

import ModalCaja from "./ModalCaja";

import "../css/tablecajas.css";
import "../css/cajas.css";

const REGISTROS_POR_PAGINA = 10;
const PAGINAS_VISIBLES = 4;

const formatARS = (n) =>
  Number(
    n || 0
  ).toLocaleString(
    "es-AR",
    {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

const TableCajas = () => {
  const [
    rows,
    setRows,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    caja,
    setCaja,
  ] = useState({});

  const [
    show,
    setShow,
  ] = useState(false);

  const [
    busqueda,
    setBusqueda,
  ] = useState("");

  const [
    pagina,
    setPagina,
  ] = useState(1);

  /* =====================================================
     CARGA
  ===================================================== */

  const cargar =
    useCallback(
      async () => {
        setLoading(true);

        try {
          const cx =
            await getCajas();

          const list =
            (
              cx?.cajas ||
              []
            )
              .map(
                (m) => ({
                  _id:
                    String(
                      m._id
                    ),

                  activo:
                    m.activo !==
                    false,

                  codcaj:
                    m.codcaj,

                  fecha:
                    m.fecha ||
                    null,

                  descripcion:
                    m.descripcion,

                  montoInicial:
                    Number(
                      m.montoInicial ||
                        0
                    ),

                  saldoActual:
                    Number(
                      m.saldoActual ||
                        0
                    ),
                })
              )
              .sort(
                (
                  a,
                  b
                ) =>
                  new Date(
                    b.fecha ||
                      0
                  ) -
                    new Date(
                      a.fecha ||
                        0
                    ) ||
                  Number(
                    b.codcaj ||
                      0
                  ) -
                    Number(
                      a.codcaj ||
                        0
                    )
              );

          setRows(list);
        } catch (e) {
          console.error(
            "Error cargando cajas:",
            e
          );

          setRows([]);
        } finally {
          setLoading(false);
        }
      },
      []
    );

  useEffect(() => {
    cargar();
  }, [cargar]);

  /* =====================================================
     EDICIÓN
  ===================================================== */

  const handleClose = () => {
    setShow(false);

    cargar();
  };

  const handleShow = () =>
    setShow(true);

  const modificaCaja = (
    id
  ) => {
    getCajaById(
      id
    ).then(
      (resp) => {
        if (
          resp?.ok
        ) {
          setCaja(
            resp.caja
          );

          handleShow();
        } else {
          console.error(
            "❌ Error al obtener caja:",
            resp?.error ||
              resp?.msg
          );
        }
      }
    );
  };

  const eliminarCaja = (
    id
  ) => {
    if (
      !window.confirm(
        "¿Está seguro que desea borrar la caja?"
      )
    ) {
      return;
    }

    deleteCaja(
      id
    ).then(
      cargar
    );
  };

  /* =====================================================
     FILTRO
  ===================================================== */

  const cajasActivas =
    useMemo(
      () =>
        rows.filter(
          (c) =>
            c.activo
        ),
      [rows]
    );

  const cajasFiltradas =
    useMemo(() => {
      const texto =
        busqueda
          .trim()
          .toLowerCase();

      if (!texto) {
        return cajasActivas;
      }

      return cajasActivas.filter(
        (c) =>
          String(
            c.codcaj ||
              ""
          )
            .toLowerCase()
            .includes(
              texto
            ) ||
          String(
            c.descripcion ||
              ""
          )
            .toLowerCase()
            .includes(
              texto
            )
      );
    }, [
      cajasActivas,
      busqueda,
    ]);

  useEffect(() => {
    setPagina(1);
  }, [busqueda]);

  /* =====================================================
     PAGINACIÓN
  ===================================================== */

  const totalPaginas =
    Math.max(
      1,
      Math.ceil(
        cajasFiltradas.length /
          REGISTROS_POR_PAGINA
      )
    );

  useEffect(() => {
    if (
      pagina >
      totalPaginas
    ) {
      setPagina(
        totalPaginas
      );
    }
  }, [
    pagina,
    totalPaginas,
  ]);

  const inicio =
    (
      pagina -
      1
    ) *
    REGISTROS_POR_PAGINA;

  const fin =
    inicio +
    REGISTROS_POR_PAGINA;

  const cajasPagina =
    cajasFiltradas.slice(
      inicio,
      fin
    );

  const paginas =
    useMemo(() => {
      let primera =
        pagina - 1;

      if (
        primera <
        1
      ) {
        primera = 1;
      }

      let ultima =
        primera +
        PAGINAS_VISIBLES -
        1;

      if (
        ultima >
        totalPaginas
      ) {
        ultima =
          totalPaginas;

        primera =
          Math.max(
            1,
            ultima -
              PAGINAS_VISIBLES +
              1
          );
      }

      const resultado =
        [];

      for (
        let i = primera;
        i <= ultima;
        i++
      ) {
        resultado.push(i);
      }

      return resultado;
    }, [
      pagina,
      totalPaginas,
    ]);

  /* =====================================================
     TOTAL SALDOS
  ===================================================== */

  const saldoTotal =
    useMemo(
      () =>
        cajasFiltradas.reduce(
          (
            acc,
            item
          ) =>
            acc +
            Number(
              item.saldoActual ||
                0
            ),
          0
        ),
      [
        cajasFiltradas,
      ]
    );

  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {
    return (
      <div className="caja2-loading">
        Cargando cajas...
      </div>
    );
  }

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className="caja2-card">

      {/* TOOLBAR */}

      <div className="caja2-toolbar">
        <div className="caja2-toolbar-title">
          <h4>
            Cajas registradas
          </h4>

          <span>
            {
              cajasFiltradas.length
            }{" "}
            cajas activas
          </span>
        </div>

        <div className="caja2-search">
          <label>
            Buscar
          </label>

          <input
            type="text"
            className="form-control"
            placeholder="Nro o descripción..."
            value={
              busqueda
            }
            onChange={(
              e
            ) =>
              setBusqueda(
                e.target
                  .value
              )
            }
          />

          {busqueda && (
            <button
              type="button"
              className="caja2-clear"
              onClick={() =>
                setBusqueda(
                  ""
                )
              }
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* INFO */}

      <div className="caja2-info">
        <span>
          Mostrando{" "}
          <strong>
            {cajasFiltradas.length
              ? inicio + 1
              : 0}
          </strong>{" "}
          a{" "}
          <strong>
            {Math.min(
              fin,
              cajasFiltradas.length
            )}
          </strong>{" "}
          de{" "}
          <strong>
            {
              cajasFiltradas.length
            }
          </strong>
        </span>

        <span className="caja2-total">
          Saldo total:{" "}
          <strong>
            {formatARS(
              saldoTotal
            )}
          </strong>
        </span>

        <span>
          Página{" "}
          <strong>
            {pagina}
          </strong>{" "}
          de{" "}
          <strong>
            {
              totalPaginas
            }
          </strong>
        </span>
      </div>

      {/* TABLA */}

      <div className="caja2-table-scroll">
        <Table
          striped
          bordered
          hover
          className="table-container caja2-table"
        >
          <thead>
            <tr>
              <th className="caja2-col-actions">
                Acciones
              </th>

              <th className="caja2-col-codigo">
                Nro
              </th>

              <th>
                Fecha Caja
              </th>

              <th>
                Descripción
              </th>

              <th>
                Monto Inicial
              </th>

              <th>
                Saldo Actual
              </th>
            </tr>
          </thead>

          <tbody>
            {cajasPagina.length ===
            0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="caja2-empty"
                >
                  No se encontraron
                  cajas.
                </td>
              </tr>
            ) : (
              cajasPagina.map(
                (c) => (
                  <tr
                    key={
                      c._id
                    }
                  >
                    <td className="caja2-actions-cell">
                      <button
                        className="btn btn-primary"
                        title="Editar"
                        onClick={() =>
                          modificaCaja(
                            c._id
                          )
                        }
                      >
                        <i
                          className="fa fa-pencil-square-o"
                          aria-hidden="true"
                        />
                      </button>
                    </td>

                    <td>
                      {
                        c.codcaj
                      }
                    </td>

                    <td>
                      {c.fecha
                        ? new Date(
                            c.fecha
                          ).toLocaleDateString(
                            "es-AR",
                            {
                              day:
                                "2-digit",
                              month:
                                "2-digit",
                              year:
                                "numeric",
                            }
                          )
                        : "-"}
                    </td>

                    <td className="caja2-description">
                      {
                        c.descripcion
                      }
                    </td>

                    <td className="caja2-money">
                      {formatARS(
                        c.montoInicial
                      )}
                    </td>

                    <td className="caja2-money caja2-saldo">
                      {formatARS(
                        c.saldoActual
                      )}
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </Table>
      </div>

      {/* PAGINACIÓN */}

      <div className="caja2-pagination">
        <span className="caja2-page-size">
          10 cajas por página
        </span>

        <div className="caja2-pagination-buttons">
          <button
            type="button"
            disabled={
              pagina === 1
            }
            onClick={() =>
              setPagina(1)
            }
          >
            «
          </button>

          <button
            type="button"
            disabled={
              pagina === 1
            }
            onClick={() =>
              setPagina(
                pagina - 1
              )
            }
          >
            ‹
          </button>

          {paginas.map(
            (numero) => (
              <button
                type="button"
                key={numero}
                className={
                  numero ===
                  pagina
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setPagina(
                    numero
                  )
                }
              >
                {numero}
              </button>
            )
          )}

          <button
            type="button"
            disabled={
              pagina ===
              totalPaginas
            }
            onClick={() =>
              setPagina(
                pagina + 1
              )
            }
          >
            ›
          </button>

          <button
            type="button"
            disabled={
              pagina ===
              totalPaginas
            }
            onClick={() =>
              setPagina(
                totalPaginas
              )
            }
          >
            »
          </button>
        </div>
      </div>

      <ModalCaja
        show={show}
        handleClose={
          handleClose
        }
        caja={caja}
      />
    </div>
  );
};

export default TableCajas;